import React, { useEffect, useRef, useState } from 'react';

import { Badge, IconButton, Tooltip, useToast } from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';

import {
  downloadSkuItemsExportResultApi,
  getSkuItemsExportStatusApi,
  startSkuItemsExportApi,
} from 'features/sku-management/sku-management.api';

const POLL_MS = 100;

const ItemsExportButton = ({ exportQuery = {} }) => {
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const pollActiveRef = useRef(false);

  useEffect(() => {
    return () => {
      pollActiveRef.current = false;
    };
  }, []);

  const applyStatus = (status) => {
    const next = Math.min(100, Math.max(0, Math.round(Number(status?.progress) || 0)));
    setProgress((prev) => (next >= prev ? next : prev));
    if (status?.message) {
      setStatusMessage(status.message);
    }
  };

  const pollUntilReady = async () => {
    pollActiveRef.current = true;
    while (pollActiveRef.current) {
      const status = await getSkuItemsExportStatusApi();
      applyStatus(status);

      if (status?.error) {
        throw new Error(status.error);
      }

      if (status?.ready && !status?.isRunning) {
        applyStatus({ ...status, progress: 100 });
        return status;
      }

      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    }
    return null;
  };

  const handleExport = async () => {
    if (isExporting) return;

    setIsExporting(true);
    setProgress(0);
    setStatusMessage('Starting export…');

    try {
      const startStatus = await startSkuItemsExportApi(exportQuery);
      applyStatus(startStatus);

      await pollUntilReady();
      await downloadSkuItemsExportResultApi();
      setProgress(100);
      toast({
        title: 'Export complete',
        description: 'Your CSV download should begin shortly.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Export failed',
        description: error?.message || 'Could not export items',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      pollActiveRef.current = false;
      await new Promise((resolve) => setTimeout(resolve, 400));
      setIsExporting(false);
      setProgress(0);
      setStatusMessage('');
    }
  };

  if (isExporting) {
    const tooltip = statusMessage
      ? `${statusMessage} (${progress}%)`
      : `Exporting… ${progress}%`;

    return (
      <Tooltip label={tooltip} hasArrow>
        <Badge
          colorScheme="blue"
          variant="solid"
          borderRadius="md"
          px={2.5}
          h="40px"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          fontSize="xs"
          fontWeight="semibold"
          cursor="default"
          minW="52px"
          transition="min-width 0.15s ease"
        >
          {progress}%
        </Badge>
      </Tooltip>
    );
  }

  return (
    <Tooltip label="Export items to CSV" hasArrow>
      <IconButton
        aria-label="Export items to CSV"
        icon={<DownloadIcon />}
        variant="ghost"
        onClick={handleExport}
      />
    </Tooltip>
  );
};

export default ItemsExportButton;
